import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countryCodes } from '../mock';
import { toast } from 'sonner';
import { fetchGeolocation, submitLead, getCountryDialCode } from '../services/api';

export const PopupForm = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  // Fetch geolocation when popup opens
  useEffect(() => {
    if (isOpen && !geoData) {
      const loadGeolocation = async () => {
        setIsLoadingGeo(true);
        const result = await fetchGeolocation();
        if (result.success) {
          setGeoData(result.data);
          const dialCode = getCountryDialCode(result.data.countryCode);
          setFormData(prev => ({
            ...prev,
            countryCode: `+${dialCode}`
          }));
        }
        setIsLoadingGeo(false);
      };
      
      loadGeolocation();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      const result = await submitLead({
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.contactNumber,
        description: formData.message,
        countryDialCode: dialCode,
        country: geoData?.countryCode || 'US'
      }, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Form submitted successfully!');
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          contactNumber: '',
          message: ''
        });
        onClose();
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex">
        <div className="flex flex-col md:flex-row w-full">
          {/* Left Side - Image */}
          <div className="hidden md:flex md:w-1/2 relative bg-gradient-to-br from-orange-500 to-orange-700">
            <img 
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=1000&fit=crop" 
              alt="Legal consultation" 
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-white z-10">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold">Get Expert Guidance</h3>
                <p className="text-lg text-white/90">
                  Fill out the form and our legal experts will get back to you within 24 hours
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold">✓</span>
                    </div>
                    <span className="text-white/90">Free Initial Consultation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold">✓</span>
                    </div>
                    <span className="text-white/90">Expert Legal Advice</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold">✓</span>
                    </div>
                    <span className="text-white/90">Quick Response Time</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="md:w-1/2 flex flex-col max-h-[90vh]">
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="h-10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="h-10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                <div className="flex gap-2">
                  <Select
                    value={formData.countryCode}
                    onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                  >
                    <SelectTrigger className="w-28 h-11">
                      <SelectValue placeholder="+1" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.country} value={item.code}>
                          {item.code} {item.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    required
                    value={formData.contactNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, contactNumber: value });
                    }}
                    placeholder="5551234567"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="flex-1 h-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your legal needs..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-orange-600 hover:bg-orange-700 h-10 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
              
              {/* {!isLoadingGeo && geoData && (
                <p className="text-xs text-center text-gray-500">
                  Detected Location: {geoData.countryName}
                </p>
              )} */}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};